<!-- 柱状图配置 -->
<template>
  <div>
    <el-form-item label="图表样式" for="screen-bar-style">
      <el-select id="screen-bar-style" :value="barStyle" :disabled="main.activeLayoutLocked" aria-label="柱形图图表样式" @input="setBarStyle">
        <el-option label="经典柱形" value="classic" />
        <el-option label="横向比例条" value="rank" />
        <el-option label="横向统计条" value="axis" />
      </el-select>
    </el-form-item>
    <bar-rank-options v-if="ranked" :series="rankSeries" />
    <el-form-item v-if="!ranked" label="竖展示">
      <avue-switch type="textarea"
                   v-model="main.activeOption.category"></avue-switch>
    </el-form-item>
    <el-form-item label="配色方式">
      <el-select :value="colorMode" :disabled="main.activeLayoutLocked" aria-label="柱形图配色方式" @input="setColorMode">
        <el-option label="按系列配色" value="series" />
        <el-option label="按分类配色" value="category" />
      </el-select>
    </el-form-item>
    <el-form-item label="最大值高亮">
      <el-switch :value="maxHighlight" :disabled="main.activeLayoutLocked" aria-label="柱形图最大值高亮" @change="setMaxHighlight" />
    </el-form-item>
    <el-form-item v-if="maxHighlight" label="高亮颜色">
      <div class="bar-category-colors__color">
        <el-input :value="maxColorInput" :disabled="main.activeLayoutLocked" label="柱形图最大值颜色" @input="setMaxColor" />
        <el-color-picker :value="validColor(maxColorInput) || null" :disabled="main.activeLayoutLocked" show-alpha aria-label="选择最大值高亮颜色" @change="setMaxColor($event || defaultMaxColor)" />
      </div>
      <p class="bar-max-highlight-note">比较所有柱系列的数值，并列最大值同时高亮。</p>
    </el-form-item>
    <section v-if="colorMode === 'category'" class="bar-category-colors" aria-label="柱形图分类配色">
      <p>按分类名称匹配，分类颜色优先于系列配色；未设置的分类沿用原有颜色。</p>
      <div class="bar-category-colors__actions">
        <el-button size="mini" :disabled="main.activeLayoutLocked" @click="fillCategories">填入当前分类</el-button>
        <el-button size="mini" :disabled="main.activeLayoutLocked" @click="addColor">添加分类</el-button>
      </div>
      <div v-for="(row, index) in colorRows" :key="index" class="bar-category-colors__row">
        <div class="bar-category-colors__name">
          <el-input :value="row.name" :disabled="main.activeLayoutLocked" placeholder="分类名称，如：正在充电" :label="'第' + (index + 1) + '项分类名称'" @input="setColor(index, 'name', $event)" />
          <el-button type="text" :disabled="main.activeLayoutLocked" :aria-label="'删除第' + (index + 1) + '项分类配色'" @click="removeColor(index)">删除</el-button>
        </div>
        <div class="bar-category-colors__color">
          <el-input :value="row.color" :disabled="main.activeLayoutLocked" placeholder="#24d6ff" :label="'第' + (index + 1) + '项分类颜色'" @input="setColor(index, 'color', $event)" />
          <el-color-picker :value="validColor(row.color) || null" :disabled="main.activeLayoutLocked" show-alpha :aria-label="'选择第' + (index + 1) + '项分类颜色'" @change="setColor(index, 'color', $event || '')" />
        </div>
      </div>
      <p v-if="!colorRows.length">可填入当前分类，或手动添加分类名称和颜色。</p>
    </section>
    <el-collapse v-if="!ranked" accordion>
      <el-collapse-item title="柱体设置">
        <el-form-item label="最大宽度">
          <avue-slider v-model="main.activeOption.barWidth">
          </avue-slider>
        </el-form-item>
        <el-form-item label="圆角">
          <avue-slider v-model="main.activeOption.barRadius">
          </avue-slider>
        </el-form-item>
        <el-form-item label="最小高度">
          <avue-slider v-model="main.activeOption.barMinHeight">
          </avue-slider>
        </el-form-item>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import common from '@/config'
import BarRankOptions from './bar-rank.vue'
import { isBarRankStyle } from '@/runtime/bar-rank'
import { BAR_CATEGORY_PALETTE, BAR_MAX_HIGHLIGHT_COLOR, barCategoryColor, barCategoryNames } from '@/runtime/bar-presentation'
import { chargingDemo } from '@/runtime/charging-statistics-model'

function findChart (group, index) {
  if (!group || !group.$refs) return null
  const local = group.$refs[common.NAME + index]
  if (local) return Array.isArray(local) ? local[0] : local
  for (const child of group.$refs.groups || []) {
    const found = findChart(child, index)
    if (found) return found
  }
  return null
}
export default {
  inject: ['main'],
  components: { BarRankOptions },
  computed: {
    barStyle () { return isBarRankStyle(this.main.activeOption) ? this.main.activeOption.barStyle : 'classic' },
    ranked () { return isBarRankStyle(this.main.activeOption) },
    rankSeries () {
      const node = this.main.activeObj || {}, container = this.main.$refs && this.main.$refs.container
      const chart = findChart(container && container.$refs && container.$refs.subgroup, node.index)
      const data = chart && chart.dataChart || node.data || {}
      return Array.isArray(data.series) ? data.series : []
    },
    maxHighlight () { const value = this.main.activeOption.barMaxHighlight; return value === true || (this.barStyle === 'axis' && typeof value !== 'boolean') },
    defaultMaxColor () { return BAR_MAX_HIGHLIGHT_COLOR },
    maxColorInput () { return this.main.activeOption.barMaxColor == null ? BAR_MAX_HIGHLIGHT_COLOR : this.main.activeOption.barMaxColor },
    colorMode () { return this.main.activeOption.barColorMode === 'category' ? 'category' : 'series' },
    colorRows () { return Array.isArray(this.main.activeOption.barCategoryColors) ? this.main.activeOption.barCategoryColors.filter(row => row && typeof row === 'object' && !Array.isArray(row)) : [] }
  },
  methods: {
    setBarStyle (value) { if (!this.main.activeLayoutLocked) this.$set(this.main.activeOption, 'barStyle', ['rank', 'axis'].includes(value) ? value : 'classic') },
    validColor: barCategoryColor,
    setMaxHighlight (value) {
      if (this.main.activeLayoutLocked) return
      this.$set(this.main.activeOption, 'barMaxHighlight', value === true)
      if (value === true && !barCategoryColor(this.main.activeOption.barMaxColor)) this.$set(this.main.activeOption, 'barMaxColor', BAR_MAX_HIGHLIGHT_COLOR)
    },
    setMaxColor (value) {
      if (!this.main.activeLayoutLocked) this.$set(this.main.activeOption, 'barMaxColor', value)
    },
    setColorMode (value) {
      if (this.main.activeLayoutLocked) return
      this.$set(this.main.activeOption, 'barColorMode', value)
      if (value === 'category' && !this.colorRows.length) this.fillCategories()
    },
    currentCategories () {
      const node = this.main.activeObj || {}, container = this.main.$refs && this.main.$refs.container
      const chart = findChart(container && container.$refs && container.$refs.subgroup, node.index)
      if (chart && chart.myChart && chart.myChart.getOption) {
        const names = barCategoryNames(chart.myChart.getOption())
        if (names.length) return names
      }
      let data = node.data || {}
      if (Number(node.dataType) === 6 && (node.parameter || {}).statisticsSource !== 'demo') {
        try { data = chargingDemo('bar', (node.parameter || {}).statisticsMetric || 'dailyEnergy') } catch (_) { /* 无有效统计配置时沿用已保存的分类 */ }
      }
      return barCategoryNames({ xAxis: { type: 'category', data: data.categories || [] } })
    },
    fillCategories () {
      if (this.main.activeLayoutLocked) return
      const rows = this.colorRows.map(row => ({ ...row })), names = new Set(rows.map(row => String(row.name == null ? '' : row.name).trim()))
      this.currentCategories().forEach(name => {
        if (!names.has(name)) { rows.push({ name, color: BAR_CATEGORY_PALETTE[rows.length % BAR_CATEGORY_PALETTE.length] }); names.add(name) }
      })
      if (rows.length !== this.colorRows.length) this.$set(this.main.activeOption, 'barCategoryColors', rows)
    },
    addColor () {
      if (this.main.activeLayoutLocked) return
      this.$set(this.main.activeOption, 'barCategoryColors', [...this.colorRows, { name: '', color: BAR_CATEGORY_PALETTE[this.colorRows.length % BAR_CATEGORY_PALETTE.length] }])
    },
    setColor (index, key, value) {
      if (this.main.activeLayoutLocked || !this.colorRows[index] || this.colorRows[index][key] === value) return
      this.$set(this.main.activeOption, 'barCategoryColors', this.colorRows.map((row, at) => at === index ? { ...row, [key]: value } : row))
    },
    removeColor (index) {
      if (!this.main.activeLayoutLocked) this.$set(this.main.activeOption, 'barCategoryColors', this.colorRows.filter((_, at) => at !== index))
    }
  }
}
</script>

<style scoped>
.bar-max-highlight-note { margin: 6px 0 0; color: var(--editor-muted, #a4b6c5); font-size: 11px; line-height: 1.6; overflow-wrap: anywhere; }
.el-select{width:100%}.bar-category-colors p{font-size:12px;line-height:1.6;color:var(--editor-muted,#a4b6c5);margin:0 0 10px;overflow-wrap:anywhere}.bar-category-colors__actions{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}.bar-category-colors__actions .el-button+.el-button{margin-left:0}.bar-category-colors__row{margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid var(--editor-border,#34495b)}.bar-category-colors__name,.bar-category-colors__color{display:flex;align-items:center;gap:6px;min-width:0}.bar-category-colors__color{margin-top:6px}.bar-category-colors__row .el-input{flex:1;min-width:0}.bar-category-colors__row .el-color-picker,.bar-category-colors__row .el-button{flex:none}
</style>
